import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useRouter } from "next/router";
// import { useAuth } from "../../context/AuthContext";
import { UserContext } from "../../context/global/user-context";
import { getLatestThreeMonthsEngagementData } from "../../helpers/utils";
import useAuth from "../../hooks/useAuth";
import { changeUserGrade } from "../../containers/global/account-settings-views";
import { ThemeContext } from "../../context";

// Define type for the assistant tools context
interface AssistantToolsContextType {
  // Connection tools
  connectWithTutor: (
    subject?: string
  ) => Promise<{ success: boolean; message: string }>;
  scheduleSession: (
    subject?: string,
    time?: string
  ) => Promise<{ success: boolean; message: string }>;

  // Information tools
  getSubjectInfo: (subject: string) => Promise<any>;
  getPustackProInfo: () => Promise<any>;
  getUserEngagement: () => Promise<any>;
  getUserGradeLevel: () => Promise<{ grade: string; stream?: string }>;

  // Setting tools
  changeGradeLevel: (
    value: string
  ) => Promise<{ success: boolean; message: string }>;
  setNotificationPreference: (
    type: string,
    enabled: boolean
  ) => Promise<{ success: boolean; message: string }>;

  // UI tools
  showTutorList: () => void;
  showUpcomingSessions: () => void;
  toggleDarkMode: (value: string) => void;

  // State indicators
  isConnectingTutor: boolean;
  isChangingSettings: boolean;
  lastSubjectDiscussed: string | null;

  // New command execution function
  executeCommand: (command: string, args: any) => Promise<any>;
}

// Create context
const AssistantToolsContext = createContext<
  AssistantToolsContextType | undefined
>(undefined);

// Context provider
export const AssistantToolsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const router = useRouter();
  const { user } = useAuth() as any;

  // State for tracking operations
  const [isConnectingTutor, setIsConnectingTutor] = useState(false);
  const [isChangingSettings, setIsChangingSettings] = useState(false);
  const [lastSubjectDiscussed, setLastSubjectDiscussed] = useState<
    string | null
  >(null);
  const [isDarkMode, setIsDarkMode] = useContext(ThemeContext).theme;

  // Add initialization state to prevent tool execution during mount
  const [isInitialized, setIsInitialized] = useState(false);

  const isAuthenticated = user?.uid !== "guest-user";

  // Load last subject discussed from local storage
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedSubject = localStorage.getItem("lastSubjectDiscussed");
      if (savedSubject) {
        try {
          setLastSubjectDiscussed(savedSubject);
        } catch (e) {
          console.error("Failed to parse last subject:", e);
        }
      }
    }
  }, []);

  // Update last subject discussed
  const updateLastSubject = useCallback((subject: string) => {
    setLastSubjectDiscussed(subject);
    if (typeof window !== "undefined") {
      localStorage.setItem("lastSubjectDiscussed", subject);
    }
  }, []);

  // Connection tools
  const connectWithTutor = useCallback(
    async (
      subject?: string
    ): Promise<{ success: boolean; message: string }> => {
      try {
        setIsConnectingTutor(true);

        // Check if user is authenticated
        if (!isAuthenticated) {
          return {
            success: false,
            message: "You need to log in before connecting with a tutor.",
          };
        }

        // If subject is provided, update last subject
        if (subject) {
          updateLastSubject(subject);
        }

        // Navigate to tutor connection page
        router.push("/tutors");

        return {
          success: true,
          message: `Navigating to connect you with ${
            subject ? `a ${subject} tutor` : "tutors"
          }.`,
        };
      } catch (error) {
        console.error("Error connecting with tutor:", error);
        return {
          success: false,
          message:
            "Sorry, there was an error connecting you with a tutor. Please try again.",
        };
      } finally {
        setIsConnectingTutor(false);
      }
    },
    [isAuthenticated, router, updateLastSubject]
  );

  const scheduleSession = useCallback(
    async (
      subject?: string,
      time?: string
    ): Promise<{ success: boolean; message: string }> => {
      try {
        // Similar implementation as connectWithTutor but for scheduling
        return {
          success: true,
          message: `Session ${subject ? `for ${subject}` : ""} ${
            time ? `at ${time}` : ""
          } scheduled successfully.`,
        };
      } catch (error) {
        console.error("Error scheduling session:", error);
        return {
          success: false,
          message:
            "Sorry, there was an error scheduling your session. Please try again.",
        };
      }
    },
    []
  );

  // Information tools
  const getSubjectInfo = useCallback(async (subject: string): Promise<any> => {
    try {
      // In a real implementation, this would fetch subject information from an API
      return {
        subject,
        available: true,
        tutorsAvailable: 5,
        nextSession: "Tomorrow at 3 PM",
      };
    } catch (error) {
      console.error(`Error fetching info for ${subject}:`, error);
      throw error;
    }
  }, []);

  const getPustackProInfo = useCallback(async (): Promise<any> => {
    try {
      // Mock implementation - would fetch from API in production
      return {
        plans: [
          {
            name: "Basic",
            price: "₹499/month",
            features: ["Unlimited questions", "Access to recorded sessions"],
          },
          {
            name: "Pro",
            price: "₹999/month",
            features: [
              "Everything in Basic",
              "1-on-1 tutoring",
              "Priority support",
            ],
          },
        ],
        userHasPro: user?.tier === "pro",
      };
    } catch (error) {
      console.error("Error fetching PuStack Pro info:", error);
      throw error;
    }
  }, [user?.tier]);

  const getUserEngagement = useCallback(async (): Promise<any> => {
    try {
      // Use the utility function to get engagement data
      // const engagementData = await getLatestThreeMonthsEngagementData();
      // return engagementData;
      return [];
    } catch (error) {
      console.error("Error fetching user engagement:", error);
      throw error;
    }
  }, []);

  const getUserGradeLevel = useCallback(async (): Promise<{
    grade: string;
  }> => {
    // Return user's current grade level from user details
    return {
      grade: user?.grade || "Unknown",
    };
  }, [user?.grade]);

  // Setting tools
  const changeGradeLevel = useCallback(
    async (value: string): Promise<{ success: boolean; message: string }> => {
      try {
        setIsChangingSettings(true);

        // Validate grade range (7-10)
        const gradeValue = parseInt(value);
        if (isNaN(gradeValue) || gradeValue < 7 || gradeValue > 10) {
          return {
            success: false,
            message: `Invalid grade value: ${value}. Grade must be between 7 and 10.`,
          };
        }

        console.log("Changing grade level:", user?.uid, value);

        // value will be like 10.
        // we need to convert it to class_10
        const grade_name = `class_${value}`;

        // Call updateUserDetails with new grade level
        await changeUserGrade(user?.uid, grade_name);

        return {
          success: true,
          message: `Your grade level has been updated to ${value}.`,
        };
      } catch (error) {
        console.error("Error changing grade level:", error);
        return {
          success: false,
          message:
            "Sorry, there was an error updating your grade level. Please try again.",
        };
      } finally {
        setIsChangingSettings(false);
      }
    },
    [user?.uid]
  );

  useEffect(() => {
    console.log("MY USer - ", user?.uid);
  }, [user?.uid]);

  const setNotificationPreference = useCallback(
    async (
      type: string,
      enabled: boolean
    ): Promise<{ success: boolean; message: string }> => {
      try {
        setIsChangingSettings(true);

        // Check if user is authenticated
        if (!isAuthenticated) {
          return {
            success: false,
            message: "You need to log in to change notification settings.",
          };
        }

        // This would update the user's notification preferences in a real implementation
        // For now, just return success
        return {
          success: true,
          message: `${type} notifications are now ${
            enabled ? "enabled" : "disabled"
          }.`,
        };
      } catch (error) {
        console.error("Error setting notification preference:", error);
        return {
          success: false,
          message:
            "Sorry, there was an error updating your notification settings. Please try again.",
        };
      } finally {
        setIsChangingSettings(false);
      }
    },
    [isAuthenticated]
  );

  // UI tools
  const showTutorList = useCallback(() => {
    router.push("/tutors");
  }, [router]);

  const showUpcomingSessions = useCallback(() => {
    router.push("/sessions");
  }, [router]);

  const toggleDarkMode = useCallback(
    (value: string) => {
      // Implementation would depend on how theme switching is handled in the app
      if (value === "toggle") {
        setIsDarkMode((c) => !c);
      } else {
        setIsDarkMode(value === "dark" ? true : false);
      }
    },
    [setIsDarkMode]
  );

  // Function to execute commands
  const executeCommand = useCallback(
    async (command: string, args: any) => {
      console.log(`Executing command: ${command} with args:`, args);

      try {
        let result;

        switch (command) {
          case "changeSetting":
            // Handle setting changes from Gemini API
            if (args.setting === "grade") {
              console.log(`Changing grade to: ${args.value}`);

              // Validate grade range (7-10)
              const gradeValue = parseInt(args.value);
              if (isNaN(gradeValue) || gradeValue < 7 || gradeValue > 10) {
                return {
                  success: false,
                  error: `Invalid grade value: ${args.value}. Grade must be between 7 and 10.`,
                };
              }

              // Implementation depends on your app's state management
              // You might want to call a context method or API to update the grade
              try {
                const result = await changeGradeLevel(args.value);

                return {
                  success: result.success,
                  message: result.message,
                  ...(result.success ? {} : { error: result.message }),
                };
              } catch (error) {
                console.error("Error in changeGradeLevel:", error);
                return {
                  success: false,
                  error: `Failed to change grade: ${error.message}`,
                };
              }
            } else if (args.setting === "theme") {
              console.log(`Changing theme to: ${args.value}`);
              // Toggle theme implementation
              await toggleDarkMode(args.value);
              return {
                success: true,
                message: `Theme changed to ${args.value}`,
              };
            } else {
              console.warn(`Unknown setting: ${args.setting}`);
              return {
                success: false,
                error: `Unknown setting: ${args.setting}`,
              };
            }

          case "Maps_to_page":
            // Handle navigation from Gemini API
            let navigationPath = "/";

            if (args.destination === "home") {
              navigationPath = "/";
            } else if (args.destination === "blaze") {
              navigationPath = "/blaze";
            } else if (args.destination === "classes") {
              navigationPath = "/classes";
            } else if (args.destination === "classroom" && args.context_id) {
              navigationPath = `/classroom/${args.context_id}`;
            } else if (args.destination === "settings") {
              navigationPath = "/settings";
            } else {
              navigationPath = `/${args.destination}`;
            }

            result = await router.push(navigationPath);
            return {
              success: true,
              result,
              message: `Navigated to ${args.destination}`,
            };

          case "navigateClient":
            // Handle legacy navigation commands
            result = await router.push({
              pathname: args.path || args.destination || "/",
              query: args.query ? JSON.parse(args.query) : undefined,
            });
            return { success: true, result };

          case "getInfo":
            // Handle information requests
            if (args.infoType === "proSubscription") {
              return {
                success: true,
                message: "Pro subscription information retrieved",
                data: {
                  /* Add actual pro subscription data */
                },
              };
            } else if (args.infoType === "userGrade") {
              return {
                success: true,
                message: "User grade information retrieved",
                data: {
                  /* Add actual grade data */
                },
              };
            } else {
              return {
                success: true,
                message: "Information retrieved",
                data: { infoType: args.infoType },
              };
            }

          case "subscribeNotification":
            // Handle notification subscription
            console.log(
              `Subscribing to notifications: ${args.notificationType}`
            );
            return {
              success: true,
              message: `Subscribed to ${args.notificationType} notifications`,
            };

          // Legacy commands for backward compatibility
          case "navigate":
            result = await router.push({
              pathname: args.path,
              query: args.query ? JSON.parse(args.query) : undefined,
            });
            return { success: true, result };

          case "openLecture":
            result = await router.push({
              pathname: `/lecture/${args.lectureId}`,
            });
            return { success: true, result };

          case "playVideo":
            const videoPath = args.videoId.includes("http")
              ? `/video/external?url=${encodeURIComponent(args.videoId)}`
              : `/video/${args.videoId}`;

            result = await router.push({
              pathname: videoPath,
              query: args.startTime ? { t: args.startTime } : undefined,
            });
            return { success: true, result };

          case "openBlaze":
            result = await router.push({
              pathname: "/blaze",
              query: args.topic ? { topic: args.topic } : undefined,
            });
            return { success: true, result };

          // Note: requestBlazeSession is now handled via show_feedback_ui approach in the Blaze buddy
          // instead of function calls. The frontend shows a booking UI when show_feedback_ui: true
          // and session_request_data is provided in the response.

          case "switchGrade":
            // Legacy grade switching
            console.log(`Switching to grade: ${args.grade}`);
            return {
              success: true,
              message: `Switched to grade ${args.grade}`,
            };

          case "toggleTheme":
            // Legacy theme toggle
            const themeToggleEvent = new CustomEvent("toggleTheme");
            window.dispatchEvent(themeToggleEvent);
            return { success: true };

          case "openSettings":
            result = await router.push({
              pathname: "/settings",
              query: args.section ? { section: args.section } : undefined,
            });
            return { success: true, result };

          case "search":
            result = await router.push({
              pathname: "/search",
              query: {
                q: args.query,
                filter: args.filter || undefined,
              },
            });
            return { success: true, result };

          default:
            console.warn(`Unknown command: ${command}`);
            return { success: false, error: `Unknown command: ${command}` };
        }
      } catch (error) {
        console.error(`Error executing command ${command}:`, error);
        return { success: false, error: String(error) };
      }
    },
    [changeGradeLevel, router, toggleDarkMode]
  );

  // Listen for custom events from the global executeAssistantTool function
  useEffect(() => {
    console.log(
      "🎧 Setting up assistant:tool event listener, isInitialized:",
      isInitialized
    );

    // Define event listener function with correct typing
    function handleToolEvent(event: Event) {
      // Cast to custom event
      const customEvent = event as CustomEvent<{
        command: string;
        args: Record<string, any>;
      }>;
      const { command, args } = customEvent.detail;

      console.log(
        "🔧 Received tool event:",
        command,
        args,
        "isInitialized:",
        isInitialized
      );

      // Prevent tool execution during initialization to avoid unwanted modals
      if (!isInitialized) {
        console.log("⏸️ Ignoring tool event during initialization:", command);
        // Dispatch a "skipped" result to prevent hanging promises
        window.dispatchEvent(
          new CustomEvent("assistant:tool:result", {
            detail: {
              command,
              result: {
                success: false,
                message: "Tool execution skipped during initialization",
              },
              error: null,
            },
          })
        );
        return;
      }

      console.log("▶️ Executing tool command:", command);
      // Execute the command and handle the result/error
      executeCommand(command, args)
        .then((result) => {
          console.log("✅ Tool command completed with result:", result);
          // Dispatch result event
          window.dispatchEvent(
            new CustomEvent("assistant:tool:result", {
              detail: {
                command,
                result,
                error: result.success ? null : result.error,
              },
            })
          );
        })
        .catch((error) => {
          console.error("❌ Error executing tool command:", error);

          // Dispatch error event
          window.dispatchEvent(
            new CustomEvent("assistant:tool:result", {
              detail: {
                command,
                result: null,
                error: String(error),
              },
            })
          );
        });
    }

    console.log("🎧 Adding event listener for assistant:tool");
    // Add event listener
    window.addEventListener("assistant:tool", handleToolEvent);
    console.log("🎧 Event listener added successfully");

    // Cleanup
    return () => {
      console.log("🎧 Removing event listener for assistant:tool");
      window.removeEventListener("assistant:tool", handleToolEvent);
    };
  }, [executeCommand, isInitialized]); // Add isInitialized to dependencies

  // useEffect(() => {
  //   console.log("Test | connectWithTutor - ");
  // }, [connectWithTutor]);

  // useEffect(() => {
  //   console.log("Test | scheduleSession - ");
  // }, [scheduleSession]);

  // useEffect(() => {
  //   console.log("Test | getSubjectInfo - ");
  // }, [getSubjectInfo]);

  // useEffect(() => {
  //   console.log("Test | getPustackProInfo - ");
  // }, [getPustackProInfo]);

  // useEffect(() => {
  //   console.log("Test | getUserEngagement - ");
  // }, [getUserEngagement]);

  // useEffect(() => {
  //   console.log("Test | getUserGradeLevel - ");
  // }, [getUserGradeLevel]);

  // useEffect(() => {
  //   console.log("Test | changeGradeLevel - ");
  // }, [changeGradeLevel]);

  // useEffect(() => {
  //   console.log("Test | setNotificationPreference - ");
  // }, [setNotificationPreference]);

  // useEffect(() => {
  //   console.log("Test | showTutorList - ");
  // }, [showTutorList]);

  // useEffect(() => {
  //   console.log("Test | showUpcomingSessions - ");
  // }, [showUpcomingSessions]);

  // useEffect(() => {
  //   console.log("Test | toggleDarkMode - ");
  // }, [toggleDarkMode]);

  // useEffect(() => {
  //   console.log("Test | isConnectingTutor - ");
  // }, [isConnectingTutor]);

  // useEffect(() => {
  //   console.log("Test | isChangingSettings - ");
  // }, [isChangingSettings]);

  // useEffect(() => {
  //   console.log("Test | lastSubjectDiscussed - ");
  // }, [lastSubjectDiscussed]);

  // useEffect(() => {
  //   console.log("Test | executeCommand - ");
  // }, [executeCommand]);

  // Combine all tools into context value
  const value: AssistantToolsContextType = useMemo(() => {
    return {
      connectWithTutor,
      scheduleSession,
      getSubjectInfo,
      getPustackProInfo,
      getUserEngagement,
      getUserGradeLevel,
      changeGradeLevel,
      setNotificationPreference,
      showTutorList,
      showUpcomingSessions,
      toggleDarkMode,
      isConnectingTutor,
      isChangingSettings,
      lastSubjectDiscussed,
      executeCommand,
    };
  }, [
    connectWithTutor,
    scheduleSession,
    getSubjectInfo,
    getPustackProInfo,
    getUserEngagement,
    getUserGradeLevel,
    changeGradeLevel,
    setNotificationPreference,
    showTutorList,
    showUpcomingSessions,
    toggleDarkMode,
    isConnectingTutor,
    isChangingSettings,
    lastSubjectDiscussed,
    executeCommand,
  ]);

  // Mark as initialized after component mounts
  useEffect(() => {
    console.log("🔧 AssistantToolsProvider mounting...");

    const timer = setTimeout(() => {
      console.log(
        "✅ AssistantToolsProvider initialized - tool execution enabled"
      );
      setIsInitialized(true);
    }, 1000); // Wait 1 second before allowing tool execution

    return () => {
      console.log("🔧 AssistantToolsProvider unmounting...");
      clearTimeout(timer);
    };
  }, []);

  return (
    <AssistantToolsContext.Provider value={value}>
      {children}
    </AssistantToolsContext.Provider>
  );
};

// Custom hook for using assistant tools
export const useAssistantTools = () => {
  const context = useContext(AssistantToolsContext);
  if (context === undefined) {
    throw new Error(
      "useAssistantTools must be used within an AssistantToolsProvider"
    );
  }
  return context;
};

// Custom hook for executing assistant commands
export const useAssistantCommands = () => {
  const {
    connectWithTutor,
    changeGradeLevel,
    setNotificationPreference,
    getPustackProInfo,
    executeCommand: _executeCommand,
    toggleDarkMode,
  } = useAssistantTools();

  // Command execution function that properly uses the React hook context
  const executeCommand = useCallback(
    async (command: string, args: any): Promise<any> => {
      switch (command) {
        case "connect_tutor":
          return await connectWithTutor(args?.subject);
        case "change_grade":
          return await changeGradeLevel(args?.value);
        case "set_notification":
          return await setNotificationPreference(args?.type, args?.enabled);
        case "get_pro_info":
          return await getPustackProInfo();
        case "toggle_dark_mode":
          toggleDarkMode(args?.value);
          return { success: true, message: "Theme toggled" };
        default:
          return await _executeCommand(command, args);
      }
    },
    [
      _executeCommand,
      connectWithTutor,
      changeGradeLevel,
      setNotificationPreference,
      getPustackProInfo,
      toggleDarkMode,
    ]
  );

  return { executeCommand };
};

// Export non-hook utilities for message processing
export const assistantTools = {
  // Function to process assistant messages for tool calls
  processMessage: (
    message: string
  ): { hasToolCall: boolean; command?: string; args?: any } => {
    // Simple regex to detect tool calls in the format: !tool_name{arg1:value,arg2:value}
    const toolCallRegex = /!([a-z_]+)\{([^}]+)\}/g;
    const match = toolCallRegex.exec(message);

    if (match) {
      const command = match[1];
      const argsString = match[2];

      // Parse args from string format
      const args = argsString.split(",").reduce((obj: any, pair: string) => {
        const [key, value] = pair.trim().split(":");
        obj[key.trim()] = value.trim();
        return obj;
      }, {});

      return { hasToolCall: true, command, args };
    }

    return { hasToolCall: false };
  },

  // Add a convenience function to format tool call strings
  formatToolCall: (command: string, args: Record<string, any>): string => {
    const argsString = Object.entries(args)
      .map(([key, value]) => `${key}:${value}`)
      .join(",");

    return `!${command}{${argsString}}`;
  },
};

// Example usage component - for documentation only
// const AssistantMessage = ({ message }) => {
//   const { executeCommand } = useAssistantCommands();
//
//   useEffect(() => {
//     const handleToolCalls = async () => {
//       const { hasToolCall, command, args } = assistantTools.processMessage(message);
//       if (hasToolCall && command) {
//         const result = await executeCommand(command, args);
//         // Handle result...
//       }
//     };
//
//     handleToolCalls();
//   }, [message, executeCommand]);
//
//   return <div>{message}</div>;
// };

export default assistantTools;
